import { useEffect, useState } from "react"
import { Check, ChevronDown, ExternalLink, Eye } from "lucide-react"
import { useSearchParams } from "react-router-dom"
import Card from "../components/Card"
import StatusPill from "../components/StatusPill"
import AddInvoiceModal from "../components/AddInvoiceModal"
import type { Invoice } from "../types"

type InvoiceCreatePayload = {
  clientId: string
  amount: number
  description: string
  due_date: string
}

const statusTone = (status: string): "success" | "warning" | "danger" | "neutral" => {
  if (status === "Paid" || status === "paid") return "success"
  if (status === "Overdue" || status === "overdue") return "danger"
  if (status === "Sent" || status === "open") return "warning"
  return "neutral"
}

const BillingPage = () => {
  const [searchParams] = useSearchParams()
  const [invoices, setInvoices] = useState<(Invoice & { source?: string })[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isAddModalOpen, setIsAddModalOpen] = useState(false)
  const [openDropdownId, setOpenDropdownId] = useState<string | null>(null)
  const invoiceParam = searchParams.get("invoice")

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (openDropdownId && !(event.target as Element).closest('.invoice-dropdown')) {
        setOpenDropdownId(null)
      }
    }
    document.addEventListener('click', handleClickOutside)
    return () => document.removeEventListener('click', handleClickOutside)
  }, [openDropdownId])

  const fetchInvoices = async () => {
    try {
      const res = await fetch("/api/invoices")
      const data = await res.json()
      if (data.invoices) {
        setInvoices(data.invoices)
      }
    } catch (error) {
      console.error("Failed to fetch invoices", error)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    fetchInvoices()
  }, [])

  useEffect(() => {
    if (invoiceParam && invoices.some((invoice) => invoice.id === invoiceParam)) {
      setOpenDropdownId(invoiceParam)
    }
  }, [invoiceParam, invoices])

  const handleCreateInvoice = async (invoiceData: InvoiceCreatePayload) => {
    try {
      const res = await fetch("/api/invoices", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(invoiceData),
      })
      
      const data = await res.json()
      if (res.ok) {
        setIsAddModalOpen(false)
        fetchInvoices()
      } else {
        alert(data.error || "Failed to create invoice")
      }
    } catch (error) {
      console.error("Failed to create invoice", error)
      alert("Failed to create invoice")
    }
  }

  const totals = invoices.reduce(
    (acc, invoice) => {
      // Normalize status
      const status = invoice.status.toLowerCase()
      acc[status] = (acc[status] ?? 0) + 1
      return acc
    },
    {} as Record<string, number>,
  )

  return (
    <div className="space-y-6">
      <Card>
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="text-lg font-semibold text-slate-900">Client Billing Hub</p>
            <p className="text-sm text-stone">
              Draft → Sent → Paid → Overdue with Stripe-ready syncing.
            </p>
          </div>
          <button
            type="button"
            onClick={() => setIsAddModalOpen(true)}
            className="rounded-full bg-moss px-4 py-2 text-sm font-semibold text-white hover:bg-moss/90 transition"
          >
            Create invoice
          </button>
        </div>
        <div className="mt-4 flex flex-wrap gap-3">
          {Object.entries(totals).map(([status, count]) => (
            <StatusPill key={status} tone={statusTone(status)} label={`${status}: ${count}`} />
          ))}
        </div>
      </Card>

      <Card>
        <div className="flex items-center justify-between">
          <p className="text-lg font-semibold text-slate-900">Invoice pipeline</p>
          <StatusPill tone="success" label="Stripe sync ready" />
        </div>
        <div className="mt-4 space-y-3">
          {isLoading ? (
            <p className="text-center text-stone py-8">Loading invoices...</p>
          ) : invoices.length === 0 ? (
            <p className="text-center text-stone py-8">No invoices found</p>
          ) : (
            invoices.map((invoice) => (
              <div
                key={invoice.id}
                className={`flex flex-wrap items-center justify-between gap-4 rounded-2xl border px-4 py-3 text-sm ${
                  invoiceParam === invoice.id ? "border-moss bg-moss/5" : "border-line bg-white"
                }`}
              >
                <div>
                  <p className="font-semibold text-slate-900">{invoice.client}</p>
                  <p className="text-xs text-stone">
                    {invoice.project} · {invoice.id}
                  </p>
                </div>
                <div className="text-right">
                  <p className="font-semibold text-slate-900">{invoice.amount}</p>
                  <p className="text-xs text-stone">
                    Issued {invoice.issued} · Due {invoice.due}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                    <StatusPill tone={statusTone(invoice.status)} label={invoice.status} />
                    
                    <div className="relative invoice-dropdown">
                        <button
                            onClick={(e) => {
                                e.stopPropagation()
                                setOpenDropdownId(openDropdownId === invoice.id ? null : invoice.id)
                            }}
                            className="flex items-center gap-1 rounded-lg border border-line bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
                        >
                            View
                            <ChevronDown className="h-3 w-3 text-stone" />
                        </button>

                        {openDropdownId === invoice.id && (
                            <div className="absolute right-0 top-full z-10 mt-1 w-48 overflow-hidden rounded-xl border border-line bg-white shadow-xl animate-in fade-in zoom-in-95">
                                {invoice.url && (
                                    <a
                                        href={invoice.url}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="flex w-full items-center gap-2 px-4 py-2.5 text-xs text-slate-700 hover:bg-slate-50"
                                    >
                                        <Eye className="h-3.5 w-3.5 text-stone" />
                                        View Client Invoice
                                    </a>
                                )}
                                {(invoice.source === "Stripe" || invoice.id.startsWith("in_")) && (
                                    <a
                                        href={`https://dashboard.stripe.com/invoices/${invoice.id}`}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="flex w-full items-center gap-2 border-t border-line px-4 py-2.5 text-xs text-slate-700 hover:bg-slate-50"
                                    >
                                        <ExternalLink className="h-3.5 w-3.5 text-stone" />
                                        View in Stripe
                                    </a>
                                )}
                                {invoice.status !== "Paid" && invoice.source !== "Stripe" && (
                                    <button
                                        type="button"
                                        onClick={async () => {
                                            try {
                                                const res = await fetch(`/api/invoices/${invoice.id}`, {
                                                    method: "PUT",
                                                    headers: { "Content-Type": "application/json" },
                                                    body: JSON.stringify({ status: "Paid" }),
                                                })
                                                if (res.ok) {
                                                    setInvoices(prev => prev.map(inv =>
                                                        inv.id === invoice.id ? { ...inv, status: "Paid" } : inv
                                                    ))
                                                }
                                            } catch { /* ignore */ }
                                            setOpenDropdownId(null)
                                        }}
                                        className="flex w-full items-center gap-2 border-t border-line px-4 py-2.5 text-xs text-green-700 hover:bg-green-50"
                                    >
                                        <Check className="h-3.5 w-3.5" />
                                        Mark as Paid
                                    </button>
                                )}
                            </div>
                        )}
                    </div>
                </div>
              </div>
            ))
          )}
        </div>
      </Card>

      <AddInvoiceModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onSave={handleCreateInvoice}
      />
    </div>
  )
}

export default BillingPage
